import { clsx } from 'clsx'
import { type ComponentPropsWithoutRef, type ReactElement } from 'react'

import { useCtaTracking } from '@lib/hooks/use-cta-tracking'

import { IconSprites } from '@constants/enums/sprites'

import { Button } from '@components/ui-core/button/button'
import { Icon } from '@components/ui-core/icon/icon'
import { Link } from '@components/ui-core/link/link'
import { Row } from '@components/ui-core/row/row'

import styles from './banner.module.scss'

type Action = {
  category: 'text' | 'primary' | 'secondary' | 'tertiary'
  url: string
  text: string
}

export type BannerProps = ComponentPropsWithoutRef<'div'> & {
  text: string
  variant?: '--purple-20'
  action?: Action
}

export function Banner({
  className,
  text,
  variant = '--purple-20',
  action,
  ...rest
}: BannerProps): ReactElement {
  const { trackAndNavigate, isLoading } = useCtaTracking({ placement: 'FloatingHeader' })

  const handleClick = (
    e: React.MouseEvent<HTMLAnchorElement> | React.MouseEvent<HTMLButtonElement>,
  ) => {
    if (!action?.url) {
      return
    }

    trackAndNavigate('Superhuman Mail Banner', action.text, action.category, action.url, e)
  }

  const actionContent =
    action &&
    (action.category === 'text' ? (
      <Link
        href={action.url}
        className={styles.actionLink}
        disabled={isLoading}
        onClick={handleClick}
      >
        {action.text}
        <Icon variant={IconSprites.BannerArrow} className={styles.actionLinkIcon} />
      </Link>
    ) : (
      <Button
        category={action.category}
        text={action.text}
        isCompact={true}
        isLoading={isLoading}
        onClick={handleClick}
      />
    ))

  return (
    <Row
      gap="1x"
      mainAxisAlignment="center"
      crossAxisAlignment="center"
      className={clsx(styles.banner, className)}
      style={{ backgroundColor: `var(${variant})` }}
      {...rest}
    >
      <span className={styles.text}>{text}</span>
      {actionContent}
    </Row>
  )
}
